import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  ShoppingBag,
  Close,
  ContentCopy,
  ReceiptLong,
  Print,
  CheckCircle,
} from '@mui/icons-material';
import { InvoiceModal, PURPLE } from '../../common/InvoiceDocument';

// Mock order data with full details
const initialOrders = [
  {
    id: '#ORD-7742',
    date: 'Oct 12, 2026',
    total: '₹1,540.00',
    total_amount: 1540,
    status: 'Delivered',
    payment_status: 'Paid',
    items: [
      { description: 'Windows 11 Pro Retail Key', quantity: 1, unit_price: 990, total_price: 990, hsn_sac: '997331' },
      { description: 'MS Office Home & Business 2024', quantity: 1, unit_price: 450, total_price: 450, hsn_sac: '997331' },
      { description: 'Windows Defender Security Upgrade', quantity: 1, unit_price: 100, total_price: 100, hsn_sac: '997331' },
    ],
    deliveryAddress: '101 Oxford Towers, HAL Old Airport Rd, Bangalore, KA 560008',
    customer_name: 'Priya Sharma',
    email: 'priya@example.com',
    phone: '+91 98765 43210',
    paymentMethod: 'Cashfree Payment Gateway (Prepaid)',
    timeline: ['Order Placed', 'Payment Verified', 'License Key Generated', 'Email & WhatsApp Dispatched'],
    activeStep: 4,
  },
  {
    id: '#ORD-8821',
    date: 'Sep 28, 2026',
    total: '₹450.00',
    total_amount: 450,
    status: 'Processing',
    payment_status: 'Paid',
    items: [
      { description: 'MS Office Professional Plus 2021', quantity: 1, unit_price: 450, total_price: 450, hsn_sac: '997331' },
    ],
    deliveryAddress: '45 Koramangala 5th Block, Bangalore, KA 560095',
    customer_name: 'Rahul Verma',
    email: 'rahul@example.com',
    phone: '+91 87654 32109',
    paymentMethod: 'Cashfree Payment Gateway (Prepaid)',
    timeline: ['Order Placed', 'Payment Verified', 'License Key Generated', 'Email & WhatsApp Dispatched'],
    activeStep: 2,
  },
  {
    id: '#ORD-9910',
    date: 'Aug 15, 2026',
    total: '₹2,100.00',
    total_amount: 2100,
    status: 'Cancelled',
    payment_status: 'Cancelled',
    items: [
      { description: 'Windows Server 2025 Standard', quantity: 2, unit_price: 800, total_price: 1600, hsn_sac: '997331' },
      { description: 'Visual Studio Enterprise 2024', quantity: 1, unit_price: 500, total_price: 500, hsn_sac: '997331' },
    ],
    deliveryAddress: '20 Whitefield Main Rd, Bangalore, KA 560066',
    customer_name: 'Ananya Reddy',
    email: 'ananya@example.com',
    phone: '+91 76543 21098',
    paymentMethod: 'Online UPI Transfer',
    timeline: ['Order Placed', 'Payment Verified', 'License Key Generated', 'Email & WhatsApp Dispatched'],
    activeStep: 0,
  },
];

const statusColor = (status) => {
  if (status === 'Delivered') return { bg: '#E8F5E9', color: '#2D6A4F' };
  if (status === 'Cancelled') return { bg: '#FFEBEE', color: '#D32F2F' };
  return { bg: '#FFF3E0', color: '#F57C00' };
};

const OrderHistory = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingBag sx={{ color: 'primary.main', fontSize: 30 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A237E' }}>
              Order History & Invoices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View order status and download official tax invoices anytime
            </Typography>
          </Box>
        </Box>
      </Box>

      {initialOrders.length > 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2.5 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {initialOrders.map((order) => {
                const sc = statusColor(order.status);
                return (
                  <TableRow key={order.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 700, color: '#1A237E' }}>
                      {order.id}
                    </TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{order.total}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        sx={{ borderRadius: 1.5, fontWeight: 700, bgcolor: sc.bg, color: sc.color }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="View Order Details">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedOrder(order)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Tax Invoice">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReceiptLong sx={{ fontSize: 16 }} />}
                            onClick={() => setInvoiceModalOrder(order)}
                            sx={{
                              borderColor: PURPLE,
                              color: PURPLE,
                              fontWeight: 700,
                              textTransform: 'none',
                              fontSize: '12px',
                              borderRadius: 1.5,
                              px: 1.5,
                              py: 0.4,
                              '&:hover': { borderColor: '#6454DB', bgcolor: '#F4F3FB' },
                            }}
                          >
                            Tax Invoice
                          </Button>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">You haven't placed any orders yet.</Typography>
        </Box>
      )}

      {/* ── Order Detail Dialog ── */}
      <Dialog
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1A237E' }}>
                    {selectedOrder.id}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopy(selectedOrder.id)} title="Copy">
                    <ContentCopy sx={{ fontSize: 16 }} />
                  </IconButton>
                  {copied && <Typography variant="caption" color="success.main">Copied!</Typography>}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Placed on {selectedOrder.date}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={selectedOrder.status}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: statusColor(selectedOrder.status).bg,
                    color: statusColor(selectedOrder.status).color,
                  }}
                />
                <IconButton onClick={() => setSelectedOrder(null)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                {/* Order Timeline */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#1A237E' }}>
                    FULFILLMENT STATUS
                  </Typography>
                  <Stepper activeStep={selectedOrder.activeStep} alternativeLabel>
                    {selectedOrder.timeline.map((label) => (
                      <Step key={label}>
                        <StepLabel
                          StepIconProps={{
                            sx: { '&.Mui-completed': { color: 'success.main' }, '&.Mui-active': { color: 'primary.main' } },
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {label}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                {/* Items */}
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#1A237E' }}>
                    ITEMS ORDERED
                  </Typography>
                  {selectedOrder.items.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: '#F8FAFC',
                        borderRadius: 2,
                        border: '1px solid #EEF2F6',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty: {item.quantity} | SAC: {item.hsn_sac}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A237E' }}>
                        ₹ {item.total_price.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontWeight: 700 }}>Total Paid</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#1A237E', fontSize: '16px' }}>
                      {selectedOrder.total}
                    </Typography>
                  </Box>
                </Grid>

                {/* Delivery Details */}
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#1A237E' }}>
                    DELIVERY & BILLING
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#FAFAFE' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Customer Name
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {selectedOrder.customer_name}
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Delivery Address
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {selectedOrder.deliveryAddress}
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Contact
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.phone}
                    </Typography>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Payment Gateway
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.paymentMethod}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ p: 2.5, gap: 1.5, justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                startIcon={<ReceiptLong />}
                onClick={() => setInvoiceModalOrder(selectedOrder)}
                sx={{
                  backgroundColor: PURPLE,
                  '&:hover': { backgroundColor: '#6454DB' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Download Official Tax Invoice
              </Button>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setSelectedOrder(null)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  component="a"
                  href={`/track-order`}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Track Order
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Tax Invoice Download & Print Modal ── */}
      <InvoiceModal
        open={!!invoiceModalOrder}
        onClose={() => setInvoiceModalOrder(null)}
        data={invoiceModalOrder}
      />
    </Box>
  );
};

export default OrderHistory;
